// src/components/institution/manager/StaffLeadershipTab.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Users, Mail, Phone, MapPin, Award, User, Crown, Shield } from 'lucide-react';
import { institutionService } from '../../../services/institutionService';
import { StaffMemberForm } from '../forms/StaffMemberForm';

export const StaffLeadershipTab = ({ bookId, institutionData, onUpdate }) => {
  const [staffMembers, setStaffMembers] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedRole, setSelectedRole] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);

  useEffect(() => {
    loadStaffMembers();
  }, [bookId]);

  useEffect(() => {
    applyFilters();
  }, [staffMembers, searchTerm, selectedDepartment, selectedRole]);

  const loadStaffMembers = async () => {
    try {
      const staffData = await institutionService.getStaffMembers(bookId);
      setStaffMembers(staffData);
    } catch (error) {
      console.error('Error loading staff members:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...staffMembers];

    if (searchTerm) {
      filtered = filtered.filter(member =>
        member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedDepartment !== 'all') {
      filtered = filtered.filter(member => member.department === selectedDepartment);
    }

    if (selectedRole !== 'all') {
      filtered = filtered.filter(member => member.role === selectedRole);
    }

    setFilteredStaff(filtered);
  };

  const handleSaveStaffMember = async (staffData) => {
    try {
      if (editingStaff) {
        await institutionService.updateStaffMember(bookId, editingStaff.id, staffData);
      } else {
        await institutionService.createStaffMember(bookId, staffData);
      }
      
      await loadStaffMembers();
      await onUpdate();
      setShowForm(false);
      setEditingStaff(null);
    } catch (error) {
      console.error('Error saving staff member:', error);
      alert('Failed to save staff member');
    }
  };

  const handleDeleteStaffMember = async (staffId) => {
    if (window.confirm('Are you sure you want to delete this staff member?')) {
      try {
        await institutionService.deleteStaffMember(staffId);
        await loadStaffMembers();
        await onUpdate();
      } catch (error) {
        console.error('Error deleting staff member:', error);
        alert('Failed to delete staff member');
      }
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'leadership': return Crown;
      case 'administration': return Shield;
      case 'faculty': return User;
      case 'support': return Users;
      default: return User;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'leadership': return 'text-purple-600 bg-purple-100';
      case 'administration': return 'text-blue-600 bg-blue-100';
      case 'faculty': return 'text-green-600 bg-green-100';
      case 'support': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const departments = [...new Set(staffMembers.map(s => s.department).filter(Boolean))];
  const roles = ['leadership', 'administration', 'faculty', 'support'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="h-6 w-6 text-indigo-600" />
            Staff & Leadership
          </h2>
          <p className="text-gray-600">Manage your institution's team members and leadership</p>
        </div>

        <button
          onClick={() => setShowForm(true)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Staff Member
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search staff members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <div className="flex gap-4">
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>

            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Roles</option>
              {roles.map(role => (
                <option key={role} value={role}>
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Staff Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredStaff.map((member) => {
            const RoleIcon = getRoleIcon(member.role);
            const roleColors = getRoleColor(member.role);
            
            return (
              <div key={member.id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    {member.profileImage ? (
                      <img 
                        src={member.profileImage} 
                        alt={member.name}
                        className="h-12 w-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="h-6 w-6 text-gray-500" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-900">{member.name}</h3>
                      <p className="text-sm text-gray-500">{member.position}</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        setEditingStaff(member);
                        setShowForm(true);
                      }}
                      className="p-1 text-gray-400 hover:text-indigo-600"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteStaffMember(member.id)}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {member.email && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail className="h-4 w-4" />
                      <a href={`mailto:${member.email}`} className="hover:text-indigo-600">
                        {member.email}
                      </a>
                    </div>
                  )}
                  
                  {member.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="h-4 w-4" />
                      <a href={`tel:${member.phone}`} className="hover:text-indigo-600">
                        {member.phone}
                      </a>
                    </div>
                  )}

                  {member.department && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>{member.department}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium ${roleColors}`}>
                    <RoleIcon className="h-3 w-3" />
                    {member.role}
                  </div>

                  {member.qualifications && (
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Award className="h-3 w-3" />
                      <span>{member.qualifications}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredStaff.length === 0 && (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No staff members found</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || selectedDepartment !== 'all' || selectedRole !== 'all'
              ? 'No staff members match your current filters'
              : 'Get started by adding your first staff member'
            }
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
          >
            Add Staff Member
          </button>
        </div>
      )}

      {/* Staff Member Form Modal */}
      {showForm && (
        <StaffMemberForm
          bookId={bookId}
          staffMember={editingStaff}
          onSave={handleSaveStaffMember}
          onClose={() => {
            setShowForm(false);
            setEditingStaff(null);
          }}
        />
      )}
    </div>
  );
};